import { useCallback, useEffect, useState, useMemo } from "react";
import ReactTableWrapper from "../../components/ReactTableWrapper";
import { exportAsCSV } from "../../utils/CSVExporter";
import { exportAsPDF } from "../../utils/PDFExporter";
import { useNavigate } from "react-router-dom";
import "../../App.css";
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { getAllInvoices, printSalesInvoice } from "../../services/SalesInvoiceServices";
import { buildSalesInvoiceQueryParams } from "../../utils/listViewDateRanges";
import { getDefaultListViewConfig } from "../../config/listViewSettingsConfig";
import { bootstrapListViewLoad } from "../../utils/listViewBootstrap";
import { displayError } from "../../helpers";
import { openPdfResponse } from "../../utils/openPdfInApp";
import ListFormPageHeader from "../../components/ListFormPageHeader";
import ListPageGradientSummary from "../../components/ListPageGradientSummary";
import { buildGradientCard, formatAmount, sumField, splitRecordsByStatus, buildCancelledSummaryCard } from "../../utils/listSummaryUtils";
import moment from "moment";

function SalesInvoiceTable() {
    const InitialData = {
        records: [],
    };
    const [data, setData] = useState(InitialData);
    const navigate = useNavigate();
    const [cookies] = useCookies(['myToken']);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    const todayDateLabel = moment().format('DD-MM-YYYY');

    const { active: activeRecords, cancelled: cancelledRecords } = useMemo(
        () => splitRecordsByStatus(data.records),
        [data.records],
    );

    const highlightCard = useMemo(() => {
        const todayCount = activeRecords.filter((row) => row.invoice_date === todayDateLabel).length;
        return buildGradientCard('Today Inv', todayCount, 'today');
    }, [activeRecords, todayDateLabel]);

    const summaryCards = useMemo(() => {
        const cards = [
            buildGradientCard('Total', activeRecords.length, 'blue'),
            buildGradientCard('Invoice Amount', formatAmount(sumField(activeRecords, 'invoice_amt')), 'teal'),
        ];
        const cancelledCard = buildCancelledSummaryCard(cancelledRecords, {
            valueField: 'invoice_amt',
            valueFormatter: formatAmount,
            entityLabel: 'invoice cancelled',
        });
        if (cancelledCard) cards.push(cancelledCard);
        return cards;
    }, [activeRecords, cancelledRecords]);

    const cancelledPopup = useMemo(() => ({
        title: `Cancelled Tax Invoices (${cancelledRecords.length})`,
        emptyMessage: 'No cancelled invoices in the current list.',
        columns: [
            { key: 'invoice_no', label: 'Invoice No', align: 'center' },
            { key: 'invoice_date', label: 'Date', align: 'center' },
            { key: 'customer_name', label: 'Customer', align: 'left' },
            { key: 'project_name', label: 'Site', align: 'left' },
            { key: 'invoice_amt', label: 'Amount', align: 'right' },
        ],
        getRows: () => cancelledRecords,
        mapRow: (row) => ({
            invoice_no: row.invoice_no,
            invoice_date: row.invoice_date,
            customer_name: row.customer_name,
            project_name: row.project_name,
            invoice_amt: formatAmount(row.invoice_amt),
        }),
        footerRowBuilder: (rows) => ({
            invoice_no: 'Total',
            invoice_date: '',
            customer_name: '',
            project_name: `${rows.length} Inv`,
            invoice_amt: formatAmount(sumField(rows, 'invoice_amt')),
        }),
    }), [cancelledRecords]);

    const columns = useMemo(
        () => [
            { fieldName: 'plant_alias', headerName: 'Plant' },
            { fieldName: "doc_type", headerName: 'Doc Type' },
            { fieldName: "invoice_no", headerName: 'Invoice Number' },
            { fieldName: "invoice_date", headerName: 'Invoice Date' },
            { fieldName: "customer_name", headerName: 'Customer Name' },
            { fieldName: "project_name", headerName: 'Site Name' },
            { fieldName: "invoice_amt", headerName: 'Invoice Amount' },
            { fieldName: 'salesrep_name', headerName: 'Marketting' },
            { fieldName: "status", headerName: 'Status' },
            { fieldName: "id", headerName: "ROW_ACTIONS" },
        ],
        [],
    );

    const loadSalesInvoices = useCallback((config) => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return Promise.resolve();
        }
        const activeConfig = config || getDefaultListViewConfig('sales_invoice');
        setIsLoading(true);
        return getAllInvoices(cookies, {
            ...buildSalesInvoiceQueryParams(activeConfig),
            limit: 500,
        })
            .then((response) => {
                const tableData = (response?.invoice_list || []).map((invoice) => ({
                    plant_alias: invoice.plant.alias_name,
                    id: invoice.id,
                    invoice_amt: invoice.invoice_amt,
                    invoice_no: invoice.prefix + invoice.invoice_no,
                    invoice_date: invoice.invoice_date,
                    invoice_time: invoice.invoice_time,
                    doc_type: 'SALES',
                    order_no: invoice.sales_order.prefix + invoice.sales_order.order_no,
                    customer_name: invoice.sales_order.consignee.customer.name,
                    salesrep_name: invoice.sales_order.consignee.customer.salesrep.name,
                    consignee_name: invoice.sales_order.consignee.name,
                    project_name: invoice.sales_order.consignee.project_name,
                    pan_no: invoice.sales_order.consignee.customer.pan_no,
                    gst_no: invoice.sales_order.consignee.customer.gst_no,
                    remarks: invoice.remarks,
                    status: invoice.status ? "Active" : "Cancelled",
                }));
                setData({ records: tableData });
            })
            .catch((error) => {
                setData({ records: [] });
                displayError(error.response?.data ?? error.message, 'Failed to load Sales Invoice list');
            })
            .finally(() => setIsLoading(false));
    }, [cookies]);

    const handleSettingsSaved = useCallback((config) => {
        loadSalesInvoices(config);
    }, [loadSalesInvoices]);

    useEffect(() => {
        if (!cookies.myToken) {
            setIsLoading(false);
            return undefined;
        }
        let cancelled = false;
        bootstrapListViewLoad({
            cookies,
            viewKey: 'sales_invoice',
            loadFn: loadSalesInvoices,
            isCancelled: () => cancelled,
        });
        return () => { cancelled = true; };
    }, [cookies, cookies.myToken, loadSalesInvoices, refreshKey]);

    const onEditSalesPosting = (salespostId) => {
        const selected_invoice = data.records.filter((obj) => obj.id === salespostId);
        if (selected_invoice[0]?.remarks === 'sales_invoice') {
            navigate(`/SalesInvoiceEdit/${salespostId}/`);
        } else {
            navigate(`/DirectInvoiceEdit/${salespostId}/`);
        }
    };

    const onPrintSalesPosting = (salespostId, event) => {
        event.preventDefault();
        setIsLoading(true);
        printSalesInvoice(cookies, salespostId)
            .then((response) => {
                openPdfResponse(response, { title: 'Sales Invoice', downloadFileName: 'sales_invoice.pdf' });
                setIsLoading(false);
            })
            .catch((error) => {
                displayError(error, 'Print Failed');
                setIsLoading(false);
            });
    };

    const handleCSVExport = () => {
        exportAsCSV(
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((c) => c.headerName),
            data.records.map((invoice) => ({
                plant_alias: invoice.plant_alias,
                doc_type: invoice.doc_type,
                invoice_no: invoice.invoice_no,
                invoice_date: invoice.invoice_date,
                customer_name: invoice.customer_name,
                project_name: invoice.project_name,
                invoice_amt: invoice.invoice_amt,
                salesrep_name: invoice.salesrep_name,
            })),
        );
    };

    const handlePDFExport = () => {
        exportAsPDF(
            'List of Sales Invoice',
            columns
                .filter((col) => col.headerName !== 'ROW_ACTIONS')
                .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
            data.records.map((invoice) => ({
                plant_alias: invoice.plant_alias,
                id: invoice.id,
                invoice_amt: invoice.invoice_amt,
                invoice_no: invoice.invoice_no,
                invoice_date: invoice.invoice_date,
                invoice_time: invoice.invoice_time,
                doc_type: invoice.doc_type,
                order_no: invoice.order_no,
                customer_name: invoice.customer_name,
                salesrep_name: invoice.salesrep_name,
                consignee_name: invoice.consignee_name,
                project_name: invoice.project_name,
                pan_no: invoice.pan_no,
                gst_no: invoice.gst_no,
            })),
            'SalesInvoice.pdf',
        );
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId={'page-wrap'} outerContainerId={'outer-container'} />
            <div id="page-wrap">
                <ListFormPageHeader
                    module="ACCOUNTS"
                    title="List of Tax Invoice"
                    viewKey="sales_invoice"
                    onSettingsSaved={handleSettingsSaved}
                    formLink="/SalesInvoice"
                    addNewLabel="New Tax Invoice"
                />
                <ListPageGradientSummary
                    highlightCard={highlightCard}
                    cards={summaryCards}
                    cancelledPopup={cancelledPopup}
                />
                <div className="container item-list-table-container">
                    <div className="table-responsive">
                        <ReactTableWrapper
                            title='List of Invoice'
                            columns={columns}
                            data={data.records}
                            onRowEdit={onEditSalesPosting}
                            hideDelete={true}
                            onCSVExport={handleCSVExport}
                            onPDFExport={handlePDFExport}
                            isLoading={isLoading}
                            onRowPrint={onPrintSalesPosting}
                            hidePrint={false}
                        />
                    </div>
                </div>
            </div>
</div>
    );
}

export default SalesInvoiceTable;
